import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { ObstacleGroup, ObstacleItem, SklonjenaIsporuka } from '../types';
import Dialog from './Dialog';

/**
 * Šta ne može u turu i zašto.
 *
 * Svaki od ovih podataka je i do sada postojao, ali razbacan po ekranima — pa je
 * operater gledao praznu listu za neko mesto i nije imao gde da proveri zašto je
 * prazna. Najtiši uzrok je tura koja stoji otvorena: roba je rezervisana, a
 * nigde ne piše da je drži tura od pre tri dana.
 *
 * Uz svaku grupu stoji <b>šta se radi</b>. Spisak bez toga je samo prigovor.
 */
export default function Obstacles() {
  const [groups, setGroups] = useState<ObstacleGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [asked, setAsked] = useState<string | null>(null);

  const [sklonjene, setSklonjene] = useState<SklonjenaIsporuka[]>([]);

  const load = useCallback(async () => {
    try {
      const [result, skinute] = await Promise.all([
        api.obstacles(), api.sklonjeneIsporuke()]);
      setGroups(result);
      setSklonjene(skinute);
      setOpen((current) => current ?? result[0]?.kind ?? null);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Pustanje robe od dobavljaca u plan, na covekovu rec.
   *
   * Ide UZ RED, ne kao dugme nad grupom: tvrdi se za jednu otpremnicu — „ovu
   * sam video u magacinu" — a ne za sve odjednom.
   */
  const [busy, setBusy] = useState<string | null>(null);

  /**
   * Otpremnica koju operater prebacuje u razvoz prodavnice.
   *
   * Postoji jer se oznaka 9999013 upisuje pri kucanju računa, a tada se ne zna
   * uvek kako će roba ići: trgovac je mislio da ide logistika pa se ispostavi
   * da kupac ne može da čeka tri dana, ili je oznaku prosto zaboravio. Bez
   * ovoga bi ispravka morala u ERP, u već zaključen račun.
   */
  const [prebacujem, setPrebacujem] = useState<ObstacleItem | null>(null);
  const [prebaciRazlog, setPrebaciRazlog] = useState('');

  const prebaci = async () => {
    if (!prebacujem?.documentKey) return;

    // Objekat se čita iz ključa otpremnice: „006-1-2966" je prodao 006. Ne
    // pita se — kupca vozi ona prodavnica koja mu je i prodala.
    const objekat = prebacujem.documentKey.split('-')[0];

    setBusy(prebacujem.documentKey);
    try {
      await api.moveToLocalDelivery(
        prebacujem.documentKey, objekat, prebaciRazlog.trim());
      setPrebacujem(null);
      setPrebaciRazlog('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * „Ovo se ne vozi."
   *
   * Postoji jer ERP drži otpremnicu otvorenom dok je prodaja ne zatvori, i to
   * je ispravno — ali dok stoji, ona zauzima red na spisku koji operater gleda
   * svakog jutra. Izmereno 15.09.2026: proba radnika iz Odžaka („Kesa Elbraco
   * VELIKA", bez kupca i bez ulice) stajala je dvanaest dana u redu za potvrdu
   * adresa, a taj red se za nju ne može rešiti nikada.
   *
   * Razlog je obavezan, a odluka se vraća. Sklanjanje bez traga bilo bi isto
   * što i brisanje.
   */
  const [sklanjam, setSklanjam] = useState<ObstacleItem | null>(null);
  const [skloniRazlog, setSkloniRazlog] = useState('');

  const skloni = async () => {
    if (!sklanjam?.documentKey || !skloniRazlog.trim()) return;

    setBusy(sklanjam.documentKey);
    try {
      await api.skloniIsporuku(sklanjam.documentKey, skloniRazlog.trim());
      setSklanjam(null);
      setSkloniRazlog('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const vrati = async (documentKey: string) => {
    setBusy(documentKey);
    try {
      await api.vratiIsporuku(documentKey);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const naRec = async (documentKey: string, pusti: boolean) => {
    setBusy(documentKey);
    try {
      if (pusti) {
        await api.robaJeStigla(documentKey);
      } else {
        await api.povuciRobaJeStigla(documentKey);
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * „Kupac pristaje — vozi bez ove robe."
   *
   * Ide kroz dijalog, ne kroz golo dugme: ovo je odluka koja košta drugi izlazak
   * vozila i traži da je neko svesno donese. Ime se ne pita — uzima se iz
   * prijave — ali se pita da li kupac zaista zna.
   */
  const [deli, setDeli] = useState<ObstacleItem | null>(null);
  const [razlog, setRazlog] = useState('');

  const voziBezOstatka = async () => {
    if (!deli?.documentKey) return;

    setBusy(deli.documentKey);
    try {
      await api.voziBezOstatka(deli.documentKey, razlog.trim() || undefined);
      setDeli(null);
      setRazlog('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const vratiCelu = async (documentKey: string) => {
    setBusy(documentKey);
    try {
      await api.povuciVoziBezOstatka(documentKey);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Zamoli objekte da pošalju robu u magacin.
   *
   * Ne šalje se samo od sebe: poruka ide ljudima koji su usred posla, pa je
   * operater taj koji odlučuje da je vreme. Isti objekat dobija najviše jednu
   * poruku dnevno — spisak se ne menja od jutra do večeri.
   */
  const askStores = async () => {
    setAsking(true);
    try {
      const result = await api.askStoresToSendGoods();
      setAsked(result.javljeno === 0
        ? 'Svi objekti su već dobili poruku danas.'
        : `Javljeno objektima: ${result.objekti.join(', ')}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAsking(false);
    }
  };

  if (error) {
    return <div className="health health--bad">{error}</div>;
  }

  if (!groups) {
    return <p className="muted">Učitavam…</p>;
  }

  /* Sklonjeno stoji NA ISTOM EKRANU, ne u nekom podešavanju. Odluka koja nešto
     skida sa spiska mora da se vidi odatle odakle je skinula — inače je za
     mesec dana niko ne nađe, a red prosto „ne postoji". */
  const sklonjenoPanel = sklonjene.length > 0 && (
    <section className="panel panel--muted">
      <h2>Ne vozi se ({sklonjene.length})</h2>
      <p className="note">
        Ovo je sklonjeno sa spiskova odlukom operatera. U ERP-u otpremnice i
        dalje stoje otvorene — sklanjanje ništa ne briše i vraća se jednim klikom.
      </p>
      <ul className="rows">
        {sklonjene.map((s) => (
          <li key={s.documentKey}>
            <span className="row__name">{s.documentKey}</span>
            <span className="row__side">
              <span className="muted">{s.by ?? 'nepoznato ko'}</span>
            </span>
            <span className="row__meta">{s.reason}</span>
            <span className="row__meta">
              <button
                type="button"
                className="link"
                disabled={busy === s.documentKey}
                onClick={() => void vrati(s.documentKey)}
              >
                Vrati na spisak
              </button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );

  if (groups.length === 0) {
    return (
      <div className="obstacles">
        <section className="panel">
          <h2>Ne može u turu</h2>
          <p className="empty">
            <strong>Ništa ne stoji.</strong><br />
            Sve što čeka prevoz može u turu: adrese su potvrđene, objekti imaju
            tačku, nalozi imaju odredište, i nijedna tura ne stoji otvorena.
          </p>
        </section>
        {sklonjenoPanel}
      </div>
    );
  }

  return (
    <div className="obstacles">
      {groups.map((group) => {
        const isOpen = open === group.kind;

        return (
          <section key={group.kind} className="panel panel--todo">
            <button
              type="button"
              className="disc__head"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : group.kind)}
            >
              <span className="disc__caret" aria-hidden="true">›</span>
              <span className="disc__title">{group.title}</span>
              <span className="count">
                {group.count}
                {group.pieces > 0 && <span className="muted"> · {group.pieces} kom</span>}
              </span>
            </button>

            <p className="note">{group.whatToDo}</p>

            {/* Jedina grupa u kojoj aplikacija ume da uradi nešto umesto
                operatera: da zamoli objekte. Ostale traže njegov potez. */}
            {group.kind === 'dovlacenje' && (
              <p className="disc__action">
                <button type="button" disabled={asking} onClick={() => void askStores()}>
                  {asking ? 'Javljam…' : 'Javi objektima da pošalju'}
                </button>
                {asked && <span className="muted"> {asked}</span>}
              </p>
            )}

            {isOpen && (
              <ul className="rows disc__body">
                {group.items.map((item, index) => (
                  <li key={`${group.kind}-${index}`}>
                    <span className="row__name">{item.label}</span>

                    <span className="row__side">
                      {item.waitingDays !== null && (
                        <span className={
                          item.alarm ? 'flag flag--bad'
                            : item.waitingDays > 2 ? 'flag flag--warn' : 'flag'}>
                          {item.waitingDays === 0 ? 'danas' : `${item.waitingDays} d`}
                        </span>
                      )}
                    </span>

                    {item.detail && <span className="row__meta">{item.detail}</span>}

                    {/* Pustanje na rec ide UZ RED, ne kao dugme nad grupom:
                        tvrdi se za jednu otpremnicu, ne za sve odjednom. */}
                    {/* Pustanje na rec ide i van grupe „dobavljac": obecanje
                        poslovodje govori isto sto i oznaka 253/254 — da se
                        roba ceka, a ne da je nema. */}
                    {item.canVouch && item.documentKey && (
                      <span className="row__meta">
                        <button
                          type="button"
                          className="link"
                          disabled={busy === item.documentKey}
                          onClick={() => void naRec(item.documentKey!, true)}
                        >
                          {busy === item.documentKey ? 'Upisujem…' : 'Roba je stigla, kalkulacija još nije'}
                        </button>
                      </span>
                    )}

                    {/* Isporuka kojoj nedostaje deo robe sme da krene bez
                        nje, ali samo covekovom odlukom — kupac mora znati da
                        ce ostatak stici kasnije. */}
                    {item.canLeaveBehind && item.documentKey && (
                      <span className="row__meta">
                        <button
                          type="button"
                          className="link"
                          disabled={busy === item.documentKey}
                          onClick={() => { setDeli(item); setRazlog(''); }}
                        >
                          Kupac pristaje — vozi bez te robe
                        </button>
                      </span>
                    )}

                    {/* Prodavnica koja je prodala ume da odveze sama, i to
                        odmah. Do sada je to radila mimo aplikacije, pa vožnje
                        nije bilo nigde — izmereno 92 takve za četiri nedelje. */}
                    {item.documentKey && (
                      <span className="row__meta">
                        <button
                          type="button"
                          className="link"
                          disabled={busy === item.documentKey}
                          onClick={() => {
                            setPrebacujem(item);
                            setPrebaciRazlog('');
                          }}
                        >
                          Vozi prodavnica sama
                        </button>
                      </span>
                    )}

                    {/* Poslednja mogućnost, i namerno poslednja: sve iznad
                        pomera posao dalje, ovo ga sklanja. */}
                    {item.documentKey && (
                      <span className="row__meta">
                        <button
                          type="button"
                          className="link link--danger"
                          disabled={busy === item.documentKey}
                          onClick={() => {
                            setSklanjam(item);
                            setSkloniRazlog('');
                          }}
                        >
                          Ovo se ne vozi
                        </button>
                      </span>
                    )}

                    {group.kind === 'bez-dela' && item.documentKey && (
                      <span className="row__meta">
                        <button
                          type="button"
                          className="link link--danger"
                          disabled={busy === item.documentKey}
                          onClick={() => void vratiCelu(item.documentKey!)}
                        >
                          Povuci — vozi se cela
                        </button>
                      </span>
                    )}

                    {group.kind === 'na-rec' && item.documentKey && (
                      <span className="row__meta">
                        <button
                          type="button"
                          className="link link--danger"
                          disabled={busy === item.documentKey}
                          onClick={() => void naRec(item.documentKey!, false)}
                        >
                          Povuci potvrdu
                        </button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}

      {prebacujem && (
        <Dialog
          title="Vozi prodavnica sama"
          description={
            <>
              <strong>{prebacujem.label}</strong>
              <br /><br />
              Nalog izlazi iz tvog planiranja i pojavljuje se poslovođi na
              kartici <strong>„Moj razvoz"</strong>. Roba je kod njega, pa se
              ništa ne diže iz magacina.
              <br /><br />
              Ako prodavnica ipak ne može — vozilo u servisu, nema ko — nalog se
              vraća tebi istim dugmetom.
            </>
          }
          confirmLabel="Prebaci prodavnici"
          busy={busy === prebacujem.documentKey}
          onCancel={() => setPrebacujem(null)}
          onConfirm={() => void prebaci()}
        >
          <label>
            Zašto (može ostati prazno)
            <input
              value={prebaciRazlog}
              onChange={(e) => setPrebaciRazlog(e.target.value)}
              placeholder="npr. kupac ne može da čeka do četvrtka"
            />
          </label>
        </Dialog>
      )}

      {sklanjam && (
        <Dialog
          title="Ovo se ne vozi"
          description={
            <>
              <strong>{sklanjam.label}</strong>
              <br /><br />
              Otpremnica izlazi sa svih spiskova logistike: planiranja, reda za
              potvrdu adresa i razvoza prodavnica.
              <br /><br />
              <strong>U ERP-u se ne menja ništa</strong> — ostaje otvorena, jer
              njeno zatvaranje je stvar prodaje, ne prevoza. Odluka se vidi
              ispod, sa tvojim imenom, i vraća se jednim klikom.
            </>
          }
          confirmLabel="Skloni sa spiskova"
          danger
          // Razlog nije formalnost: bez njega se za mesec dana ne zna zašto
          // prodaje koja u ERP-u postoji nema nigde u logistici.
          disabled={!skloniRazlog.trim()}
          busy={busy === sklanjam.documentKey}
          onCancel={() => setSklanjam(null)}
          onConfirm={() => void skloni()}
        >
          <label>
            Zašto se ne vozi (obavezno)
            <input
              value={skloniRazlog}
              onChange={(e) => setSkloniRazlog(e.target.value)}
              placeholder="npr. proba radnika, nema kupca ni adrese"
            />
          </label>
        </Dialog>
      )}

      {deli && (
        <Dialog
          title="Vozi bez dela robe"
          description={
            <>
              <strong>{deli.label}</strong> — skida se sa tovara tačno ono čega
              nema nigde; ostalo ide prvom turom. Otpremnica u ERP-u ostaje
              neisporučena dok i ostatak ne stigne, pa se ne gubi, ali će vozilo
              na tu adresu izaći <strong>dva puta</strong>. Potvrdi tek kad kupac
              zna i pristaje.
            </>
          }
          confirmLabel="Kupac pristaje"
          busy={busy === deli.documentKey}
          onCancel={() => setDeli(null)}
          onConfirm={() => void voziBezOstatka()}
        >
          <label>
            Napomena (može ostati prazno)
            <input
              value={razlog}
              onChange={(e) => setRazlog(e.target.value)}
              placeholder="npr. zvao Marko, pristaje da aspirator sačeka"
            />
          </label>
        </Dialog>
      )}

      {sklonjenoPanel}
    </div>
  );
}
